import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, Typography } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { FormField, PasswordField } from './form/FormFields';
import useKeyboardInset from './form/useKeyboardInset';
import { CARD_LABEL_COLOR } from '../styles/tokens';
import ResponsiveDialog from './ResponsiveDialog';

const ACCENT = '#ff7043';
const INACTIVE = 'rgba(255,255,255,0.2)';
const SUCCESS_COLOR = '#66bb6a';
const ERROR_COLOR = '#ff6b6b';
const DEFAULT_PORT = '443';
const STEPS = ['Подключение', 'Авторизация'];
// Ждём ответа проверки не дольше, чем бэкенд ждёт iiko (10 с) + запас
const CHECK_TIMEOUT_MS = 20000;

/**
 * Адрес сервера: убираем пробелы, протокол, логин@, путь (/resto и т.п.).
 * withPort — вынести «:порт» в отдельное значение (при вставке и уходе из поля;
 * при наборе не трогаем, иначе нельзя допечатать порт).
 */
export function normalizeAddress(raw, { withPort = false } = {}) {
  let host = (raw || '').replace(/\s+/g, '');
  host = host.replace(/^[a-z][a-z0-9+.-]*:\/\//i, '');
  host = host.replace(/^[^@/]*@/, '');
  host = host.split(/[/?#]/)[0];
  let port = null;
  if (withPort) {
    const match = host.match(/^(.*):(\d{1,5})$/);
    if (match) {
      host = match[1];
      port = match[2];
    }
    host = host.replace(/:$/, '');
  }
  return { host: host.toLowerCase(), port };
}

const isValidPort = (port) => /^\d{1,5}$/.test(port) && Number(port) >= 1 && Number(port) <= 65535;

// Понятный текст ошибки проверки подключения по ответу /data/get_server_info
function describeCheckError(status, detail) {
  const raw = typeof detail === 'string' ? detail : detail?.message || '';
  if (raw === 'Invalid host format') {
    return 'Неверный адрес сервера. Укажите домен (restaurant.iiko.it) или IP-адрес.';
  }
  if (raw === 'Invalid host address') {
    return 'Адреса локальной сети не поддерживаются. Укажите внешний домен или IP-адрес.';
  }
  if (raw.startsWith('Port must be')) return 'Порт должен быть числом от 1 до 65535.';
  if (status === 429) return 'Слишком много попыток. Подождите минуту и повторите.';
  if (detail?.source === 'network' || detail?.code === 'iiko_network') {
    return 'Не удалось подключиться: сервер не отвечает или отклоняет соединение. Проверьте адрес и порт.';
  }
  if (detail?.source === 'iiko') {
    const iikoStatus = detail.status ?? status;
    return iikoStatus === 404
      ? 'По этому адресу не найден сервер iiko. Проверьте адрес и порт.'
      : `Сервер iiko ответил ошибкой${iikoStatus ? ` (${iikoStatus})` : ''}. Проверьте адрес и порт.`;
  }
  return 'Не удалось проверить подключение. Попробуйте ещё раз.';
}

function Stepper({ step }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} aria-label={`Шаг ${step + 1} из ${STEPS.length}`}>
      {STEPS.map((label, index) => {
        const reached = index <= step;
        return (
          <React.Fragment key={label}>
            {index > 0 && (
              <Box sx={{ flex: 1, height: 2, borderRadius: 1, backgroundColor: reached ? ACCENT : INACTIVE }} />
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '13px',
                  fontWeight: 700,
                  backgroundColor: reached ? ACCENT : INACTIVE,
                  color: reached ? '#1a0f0a' : 'rgba(255,255,255,0.7)',
                }}
              >
                {index + 1}
              </Box>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: index === step ? 600 : 400,
                  color: index === step ? '#fff' : CARD_LABEL_COLOR,
                }}
              >
                {label}
              </Typography>
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );
}

// Кнопки футера — общие для диалогов серверов
export const footerButtonSx = {
  height: 44,
  px: 2.5,
  borderRadius: '10px',
  textTransform: 'none',
  fontSize: '15px',
  boxShadow: 'none',
  flex: { xs: 1, md: '0 0 auto' },
  '&:hover': { boxShadow: 'none' },
};

export const primaryButtonSx = {
  ...footerButtonSx,
  backgroundColor: ACCENT,
  color: '#1a0f0a',
  fontWeight: 700,
  '&:hover': { backgroundColor: '#ff8a65', boxShadow: 'none' },
  '&.Mui-disabled': { backgroundColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.35)' },
  '&.Mui-focusVisible': { outline: '2px solid #fff', outlineOffset: '2px' },
};

export const secondaryButtonSx = {
  ...footerButtonSx,
  color: CARD_LABEL_COLOR,
  backgroundColor: 'transparent',
  '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)', boxShadow: 'none' },
};

// mode: 'create' — подключение нового сервера, 'edit' — редактирование существующего
function ServerForm({ open, serverData, handleChange, handleSubmit, onCancel, mode = 'create' }) {
  const theme = useTheme();
  const mobile = !useMediaQuery(theme.breakpoints.up('md'));
  const keyboardInset = useKeyboardInset(open && mobile);

  const [step, setStep] = useState(0);
  const [checking, setChecking] = useState(false);
  const [serverName, setServerName] = useState('');
  const [validating, setValidating] = useState(false);
  // { type: 'success' | 'error', text } — строка результата над кнопками
  const [status, setStatus] = useState(null);

  // Новое открытие окна — с первого шага
  useEffect(() => {
    if (open) {
      setStep(0);
      setStatus(null);
      setServerName('');
    }
  }, [open]);

  const host = (serverData.ip_address || '').trim();
  const port = (serverData.port || '').toString().trim();
  const busy = checking || validating;
  const canNext = Boolean(host) && isValidPort(port);
  const canSave = Boolean(serverData.login && serverData.password);
  const dirty = Boolean(
    serverData.ip_address || serverData.login || serverData.password || (port && port !== DEFAULT_PORT)
  );

  const setField = (name, value) => handleChange({ target: { name, value } });

  // Адрес изменился — прошлый результат проверки больше не актуален
  const resetCheck = () => {
    if (serverName) setServerName('');
    if (status) setStatus(null);
  };

  const applyAddress = (raw, withPort) => {
    const normalized = normalizeAddress(raw, { withPort });
    const hostChanged = normalized.host !== (serverData.ip_address || '');
    const portChanged = Boolean(normalized.port) && normalized.port !== port;
    if (hostChanged) setField('ip_address', normalized.host);
    if (portChanged) setField('port', normalized.port);
    if (hostChanged || portChanged) resetCheck();
  };

  const handleAddressPaste = (event) => {
    const text = event.clipboardData?.getData('text');
    if (!text) return;
    event.preventDefault();
    applyAddress(text, true);
  };

  const handlePortChange = (event) => {
    setField('port', event.target.value.replace(/\D/g, '').slice(0, 5));
    resetCheck();
  };

  const sha1Hash = async (text) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(text);
    const hashBuffer = await crypto.subtle.digest('SHA-1', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  };

  // Проверка подключения (то, что раньше делала кнопка «Проверить»); при успехе — шаг 2
  const handleCheck = async () => {
    if (busy || !canNext) return;
    // Адрес уже проверен и не менялся — сразу дальше
    if (serverName) {
      setStep(1);
      return;
    }
    // Нормализуем здесь же: «Далее» могли нажать, не уходя из поля адреса
    const normalized = normalizeAddress(serverData.ip_address, { withPort: true });
    const checkHost = normalized.host;
    const checkPort = normalized.port || port;
    if (checkHost !== serverData.ip_address) setField('ip_address', checkHost);
    if (checkPort !== port) setField('port', checkPort);
    if (!checkHost || !isValidPort(checkPort)) return;

    setChecking(true);
    setStatus(null);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), CHECK_TIMEOUT_MS);
    try {
      const params = new URLSearchParams({
        host: checkHost,
        port: checkPort,
        protocol: checkPort === '443' ? 'https' : 'http',
      });
      const res = await fetch(`/data/get_server_info?${params.toString()}`, { signal: controller.signal });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        const name = data?.server_name || data?.department_name || data?.name || '';
        if (name) {
          setServerName(name);
          setStatus({ type: 'success', text: `Сервер найден: ${name}` });
          setStep(1);
        } else {
          console.error('Server info response:', data);
          setStatus({ type: 'error', text: 'Сервер ответил, но это не похоже на сервер iiko. Проверьте адрес.' });
        }
      } else {
        console.error('Server check failed:', res.status, data);
        setStatus({ type: 'error', text: describeCheckError(res.status, data?.detail) });
      }
    } catch (e) {
      console.error('Error checking server:', e);
      setStatus({
        type: 'error',
        text:
          e.name === 'AbortError'
            ? 'Сервер не ответил вовремя (таймаут). Проверьте адрес и порт.'
            : 'Нет связи с сервисом. Проверьте подключение к интернету.',
      });
    } finally {
      clearTimeout(timer);
      setChecking(false);
    }
  };

  // Валидация учетных данных перед сохранением
  const validateCredentials = async () => {
    if (!serverData.login || !serverData.password) {
      return { success: false, error: 'Заполните логин и пароль' };
    }

    try {
      const hashedPassword = await sha1Hash(serverData.password);

      const response = await fetch('/data/validate_server', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ip_address: serverData.ip_address.trim(),
          port: parseInt(serverData.port),
          login: serverData.login.trim(),
          hashed_password: hashedPassword,
        }),
      });

      if (response.ok) {
        return { success: true };
      }
      const errorData = await response.json().catch(() => ({ detail: 'Ошибка авторизации' }));
      // Извлекаем текст ошибки из ответа API
      let errorMessage = 'Ошибка авторизации';
      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (errorData.detail.message) {
          errorMessage = errorData.detail.message;
        }
      }
      return { success: false, error: errorMessage };
    } catch (e) {
      console.error('Ошибка валидации:', e);
      return { success: false, error: 'Не удалось подключиться к серверу для проверки учетных данных' };
    }
  };

  const handleConnect = async (event) => {
    if (busy || !canSave) return;
    setValidating(true);
    setStatus(null);
    try {
      const validation = await validateCredentials();
      if (!validation.success) {
        setStatus({ type: 'error', text: validation.error });
        return;
      }
      await handleSubmit(event);
    } finally {
      setValidating(false);
    }
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();
    if (step === 0) {
      handleCheck();
    } else {
      handleConnect(event);
    }
  };

  const handleAuthChange = (name) => (event) => {
    setField(name, event.target.value);
    if (status?.type === 'error') setStatus(null);
  };

  const handleClose = (event, reason) => {
    // По клику на затемнение не закрываем, если что-то уже введено (Esc — закрывает)
    if (reason === 'backdropClick' && dirty) return;
    onCancel();
  };

  return (
    <ResponsiveDialog open={open} onClose={handleClose} labelledBy="server-form-title" keyboardInset={keyboardInset}>
      <Box component="form" onSubmit={handleFormSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* Шапка */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: -1, mr: -1 }}>
          <Typography id="server-form-title" component="h2" sx={{ fontSize: '18px', fontWeight: 700 }}>
            {mode === 'edit' ? 'Редактирование сервера' : 'Подключение к серверу'}
          </Typography>
          <IconButton onClick={onCancel} aria-label="Закрыть" sx={{ width: 44, height: 44, color: CARD_LABEL_COLOR }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Stepper step={step} />

        {step === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormField
              id="server-address"
              name="ip_address"
              label="Адрес сервера"
              placeholder="restaurant.iiko.it"
              hint="Домен или IP-адрес"
              value={serverData.ip_address}
              onChange={(event) => applyAddress(event.target.value, false)}
              onPaste={handleAddressPaste}
              onBlur={(event) => applyAddress(event.target.value, true)}
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              inputMode="url"
              disabled={checking}
              autoFocus
              required
            />
            <FormField
              id="server-port"
              name="port"
              label="Порт"
              value={serverData.port}
              onChange={handlePortChange}
              inputMode="numeric"
              maxLength={5}
              autoComplete="off"
              disabled={checking}
              required
            />
          </Box>
        )}

        {step === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Уникальные name + autocomplete, чтобы браузер не подставлял логин/пароль от Pro Resto */}
            <FormField
              id="iiko-login"
              name="iikoLogin"
              label="Логин iiko"
              value={serverData.login}
              onChange={handleAuthChange('login')}
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              disabled={validating}
              autoFocus
              required
            />
            <PasswordField
              id="iiko-password"
              name="iikoPassword"
              label="Пароль iiko"
              value={serverData.password}
              onChange={handleAuthChange('password')}
              autoComplete="new-password"
              disabled={validating}
              required
            />
          </Box>
        )}

        {/* Результат проверки — строкой над кнопками */}
        <Box role="status" aria-live="polite" sx={{ minHeight: status ? 'auto' : 0 }}>
          {status && (
            <Typography
              sx={{ fontSize: '14px', color: status.type === 'success' ? SUCCESS_COLOR : ERROR_COLOR }}
            >
              {status.text}
            </Typography>
          )}
        </Box>

        {/* Футер */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          {step === 0 ? (
            <Button type="button" onClick={onCancel} sx={secondaryButtonSx}>
              Отмена
            </Button>
          ) : (
            <Button
              type="button"
              onClick={() => {
                setStep(0);
                if (status?.type === 'error') setStatus(null);
              }}
              disabled={validating}
              sx={secondaryButtonSx}
            >
              Назад
            </Button>
          )}
          <Button
            type="submit"
            disabled={busy || (step === 0 ? !canNext : !canSave)}
            aria-busy={busy}
            sx={primaryButtonSx}
          >
            {busy ? (
              <CircularProgress size={20} sx={{ color: 'rgba(255,255,255,0.6)' }} />
            ) : step === 0 ? (
              'Далее'
            ) : (
              'Подключить'
            )}
          </Button>
        </Box>
      </Box>
    </ResponsiveDialog>
  );
}

export default ServerForm;
